import { Link } from "react-router";
import { _ } from "i18n";
import { formatDate, formatDateTime } from "features/discover/functions";
import type { ColumnConfig } from "../../types";
import { renderBoolean } from "../../cellRenderers";
import {
	DiscoverGamesColumnField,
	DiscoverGamesUiColumnKey,
	DISCOVER_GAMES_COL_WIDTH_DATE,
	DISCOVER_GAMES_COL_WIDTH_DATE_SHORT,
	DISCOVER_GAMES_COL_WIDTH_ID,
	DISCOVER_GAMES_COL_WIDTH_LABEL_SHORT,
	DISCOVER_GAMES_COL_WIDTH_SCORE,
	DISCOVER_GAMES_COL_WIDTH_TEAM_SHEET,
} from "../constants";
import type { GameRow } from "../types";

export function idColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.Id,
		label: _("discover.gamesTable.column.id"),
		sortable: true,
		sortField: DiscoverGamesColumnField.Id,
		minWidth: DISCOVER_GAMES_COL_WIDTH_ID.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_ID.max,
		cell: (_, row) => <Link to={`/discover/v2/game/${row.id}`}>{String(row.id)}</Link>,
		group: _("discover.gamesTable.group.basic"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.Id],
	};
}

export function dateColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.Date,
		label: _("discover.gamesTable.column.date"),
		sortable: true,
		sortField: DiscoverGamesColumnField.Date,
		minWidth: DISCOVER_GAMES_COL_WIDTH_DATE.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_DATE.max,
		cell: (value) => formatDateTime(value as string | null),
		group: _("discover.gamesTable.group.basic"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.Date],
	};
}

export function createdAtColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.CreatedAt,
		label: _("discover.gamesTable.column.createdAt"),
		sortable: true,
		sortField: DiscoverGamesColumnField.CreatedAt,
		minWidth: DISCOVER_GAMES_COL_WIDTH_DATE_SHORT.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_DATE_SHORT.max,
		cell: (value) => formatDate(value as string | null),
		group: _("discover.gamesTable.group.basic"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.CreatedAt],
	};
}

export function countryColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesUiColumnKey.Country,
		label: _("discover.gamesTable.column.country"),
		sortable: true,
		sortField: DiscoverGamesColumnField.HomeClubCountry,
		minWidth: DISCOVER_GAMES_COL_WIDTH_LABEL_SHORT.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_LABEL_SHORT.max,
		cell: (_, row) => {
			const countries = new Set<string | null | undefined>();
			countries.add(row.homeClubCountry as string | undefined);
			countries.add(row.awayClubCountry as string | undefined);
			countries.add(row.homeTeamCountry as string | undefined);
			countries.add(row.awayTeamCountry as string | undefined);
			const filtered = [...countries].filter((c): c is string => c !== null && c !== undefined);
			return <div className="thinCell">{filtered.join(", ") || "-"}</div>;
		},
		group: _("discover.gamesTable.group.basic"),
		defaultVisible: true,
		requiredFields: [
			DiscoverGamesColumnField.HomeClubCountry,
			DiscoverGamesColumnField.AwayClubCountry,
			DiscoverGamesColumnField.HomeTeamCountry,
			DiscoverGamesColumnField.AwayTeamCountry,
		],
	};
}

export function ageCategoryColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.AgeCategory,
		label: _("discover.gamesTable.column.ageCategory"),
		sortable: true,
		sortField: DiscoverGamesColumnField.AgeCategory,
		minWidth: DISCOVER_GAMES_COL_WIDTH_LABEL_SHORT.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_LABEL_SHORT.max,
		cell: (value) => <div className="discoverGamesTableV3-textCenter">{(value as string) || "-"}</div>,
		group: _("discover.gamesTable.group.basic"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.AgeCategory],
	};
}

export function scoreColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesUiColumnKey.Score,
		label: _("discover.gamesTable.column.score"),
		sortable: false,
		minWidth: DISCOVER_GAMES_COL_WIDTH_SCORE.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_SCORE.max,
		cell: (_, row) => {
			const homeScore = row.homeScore as number | null | undefined;
			const awayScore = row.awayScore as number | null | undefined;
			return (
				<div className="discoverGamesTableV3-textCenter">
					{homeScore !== null && homeScore !== undefined ? homeScore : "-"} :{" "}
					{awayScore !== null && awayScore !== undefined ? awayScore : "-"}
				</div>
			);
		},
		group: _("discover.gamesTable.group.basic"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.HomeScore, DiscoverGamesColumnField.AwayScore],
	};
}

export function teamSheetColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesUiColumnKey.TeamSheet,
		label: _("discover.gamesTable.column.teamRoster"),
		sortable: true,
		sortField: DiscoverGamesColumnField.HomeIsTeamsheetFilled,
		minWidth: DISCOVER_GAMES_COL_WIDTH_TEAM_SHEET.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_TEAM_SHEET.max,
		cell: (_, row) => {
			const homeIsFilled = row.homeIsTeamsheetFilled as boolean | undefined;
			const awayIsFilled = row.awayIsTeamsheetFilled as boolean | undefined;
			const homePlayersCount = (row.homePlayersCount as number | undefined) ?? 0;
			const awayPlayersCount = (row.awayPlayersCount as number | undefined) ?? 0;

			return (
				<div className="discoverGamesTableV3-teamSheetCell" style={{ textAlign: "center" }}>
					{renderBoolean(homeIsFilled)} ({homePlayersCount}) | {renderBoolean(awayIsFilled)} (
					{awayPlayersCount})
				</div>
			);
		},
		group: _("discover.gamesTable.group.basic"),
		defaultVisible: true,
		requiredFields: [
			DiscoverGamesColumnField.HomeIsTeamsheetFilled,
			DiscoverGamesColumnField.AwayIsTeamsheetFilled,
			DiscoverGamesColumnField.HomePlayersCount,
			DiscoverGamesColumnField.AwayPlayersCount,
		],
	};
}
